import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import AppContext from '../../../contexts/AppContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { MultiSelect } from '../../../components/ui/multi-select';
import { fetchWithAuth } from '../../../utils/fetchWithAuth';
import { Loader2, RefreshCw, Eye, EyeOff, Copy } from 'lucide-react';
import { getAllPermissions, getDefaultPermissions, usePermissionLabel } from '../../../constants/permissions';
import { generateRandomPassword } from '../../../utils/passwordUtils';
import { ROLES, useRoleLabel } from '../../../utils/roleUtils';
import { jiraProvisioningOf, provisioningNotice } from '../../../utils/jiraProvisioning';
import { useToast } from '../../../components/ui/toast';
import { getErrorMessage } from '../../../constants/errorCodes';

export const UserCreateDialog = ({ open, onClose, onUserCreated, defaultRole = 'user' }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const permissionLabel = usePermissionLabel();
  const formatRoleLabel = useRoleLabel();
  const { currentUser } = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [serviceDesks, setServiceDesks] = useState([]);
  const [selectedServiceDesks, setSelectedServiceDesks] = useState([]);

  // The API tells us what this actor may create; the UI does not re-derive it
  const [abilities, setAbilities] = useState({ creatableRoles: [], serviceDeskPickableRoles: [], canGrantPermissions: false, grantablePermissions: [] });

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: defaultRole,
    mode: 'rw',
    lang: 'fr',
    serviceDeskId: '',
    sectors: '',
    permissions: [],
  });

  const canEditServiceDesks = abilities.serviceDeskPickableRoles.includes(formData.role);
  /**
   * `/admin/technicians/new` owns that role now. The API still accepts it — the CSV import and
   * the technician form both go through it — so the filter belongs here, on the form.
   */
  const creatableRoles = abilities.creatableRoles.filter((role) => role !== ROLES.TECHNICIAN);

  // Filter permission options to only show those not included in role defaults
  const defaultPermissionsForRole = getDefaultPermissions(formData.role);
  const availablePermissionOptions = getAllPermissions()
    .filter(option => !defaultPermissionsForRole.includes(option.value))
    // Never offer a grant the API would refuse
    .filter(option => (abilities.grantablePermissions ?? []).includes(option.value))
    .map(option => ({ value: option.value, label: permissionLabel(option.value) }));

  useEffect(() => {
    if (!open) return;
    fetchAbilities();
    fetchServiceDesks();
  }, [open]);

  // Pin the actor's own desk when he may not pick one
  useEffect(() => {
    if (open && !canEditServiceDesks && currentUser?.serviceDeskId) {
      const adminServiceDeskId = Array.isArray(currentUser.serviceDeskId)
        ? currentUser.serviceDeskId.join(', ')
        : String(currentUser.serviceDeskId);
      setFormData(prev => ({ ...prev, serviceDeskId: adminServiceDeskId }));
    }
  }, [open, canEditServiceDesks, currentUser?.serviceDeskId]);

  const fetchAbilities = async () => {
    try {
      const response = await fetchWithAuth('/users/abilities');
      const json = await response.json();
      if (json.success) {
        setAbilities(json.data);
      }
    } catch (err) {
      console.error('Error fetching abilities:', err);
    }
  };

  const fetchServiceDesks = async () => {
    try {
      const response = await fetchWithAuth('/service-desks/all');
      const json = await response.json();
      if (json.success) {
        setServiceDesks(json.data);
      }
    } catch (err) {
      console.error('Error fetching service desks:', err);
    }
  };

  // Convert service desks to MultiSelect options format
  const serviceDeskOptions = serviceDesks.map(sd => ({
    value: sd.id,
    label: sd.name
  }));

  // Handle multi-select change
  const handleServiceDesksChange = (selected) => {
    setSelectedServiceDesks(selected);

    // Update formData.serviceDeskId
    if (selected.includes('*')) {
      setFormData(prev => ({ ...prev, serviceDeskId: '*' }));
    } else if (selected.length > 0) {
      setFormData(prev => ({ ...prev, serviceDeskId: selected.join(', ') }));
    } else {
      setFormData(prev => ({ ...prev, serviceDeskId: '' }));
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handlePermissionsChange = (selected) => {
    setFormData(prev => ({ ...prev, permissions: selected }));
  };

  const handleGeneratePassword = () => {
    const password = generateRandomPassword();
    handleChange('password', password);
    setShowPassword(true);
  };

  const copyPasswordToClipboard = async () => {
    if (formData.password) {
      try {
        await navigator.clipboard.writeText(formData.password);
      } catch (err) {
        console.error('Failed to copy password:', err);
      }
    }
  };

  // Reset extra permissions when role changes (defaults are applied automatically by backend)
  useEffect(() => {
    setFormData(prev => ({ ...prev, permissions: [] }));
  }, [formData.role]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Parse sectors (comma-separated string to array)
      const sectorsArray = formData.sectors
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);

      // Parse serviceDeskId - always as array: [3], [3, 205], or ["*"]
      let serviceDeskId;
      const rawServiceDeskId = formData.serviceDeskId.trim();
      if (rawServiceDeskId === '*') {
        serviceDeskId = ['*'];
      } else if (rawServiceDeskId.includes(',')) {
        serviceDeskId = rawServiceDeskId.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
      } else {
        const parsed = parseInt(rawServiceDeskId);
        if (isNaN(parsed)) {
          throw new Error(t('Invalid Service Desk ID'));
        }
        serviceDeskId = [parsed];
      }

      const payload = {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        role: formData.role,
        mode: formData.mode,
        lang: formData.lang,
        serviceDeskId,
        sectors: sectorsArray,
        permissions: formData.permissions,
      };

      const response = await fetchWithAuth('/users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const json = await response.json();

      if (!response.ok) {
        throw new Error(json.errorCode ? getErrorMessage(t, json.errorCode) : json.error || t('Creating the user failed'));
      }

      if (json.success) {
        // The account is created; Jira is the half that may have refused. The dialog closes
        // either way — the toast is what carries the caveat out with it.
        const notice = provisioningNotice(t, formData.username, jiraProvisioningOf(json.meta));
        toast[notice.tone](notice.message);
        // Reset form
        setFormData({
          username: '',
          email: '',
          password: '',
          role: defaultRole,
          mode: 'rw',
          lang: 'fr',
          serviceDeskId: '',
          sectors: '',
          permissions: [],
        });
        setSelectedServiceDesks([]);
        onUserCreated();
      } else {
        throw new Error(json.error || t('Creating the user failed'));
      }
    } catch (err) {
      console.error('Error creating user:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>{t('Create a new user')}</DialogTitle>
          <DialogDescription>
            {t('Fill in the information to create a new user account')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="space-y-4 overflow-y-auto flex-1 pr-2">
            <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="username">{t('Username *')}</Label>
              <Input
                id="username"
                value={formData.username}
                onChange={(e) => handleChange('username', e.target.value)}
                autoComplete="off"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{t('Email *')}</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                autoComplete="off"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">{t('Password *')}</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  autoComplete="off"
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={handleGeneratePassword}
                title={t('Generate a password')}
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
              {formData.password && (
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={copyPasswordToClipboard}
                  title={t('Copy the password')}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {t('Use the button to generate a secure password')}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="role">{t('Role *')}</Label>
              <Select value={formData.role} onValueChange={(value) => handleChange('role', value)}>
                <SelectTrigger id="role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {/* The API lists exactly the roles this actor may create, technicians aside:
                      they have a form of their own, which also writes the company, the phone, the
                      scope and the skills an account created here would be missing. */}
                  {creatableRoles.map((role) => (
                    <SelectItem key={role} value={role}>{formatRoleLabel(role)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="mode">{t('Mode *')}</Label>
              <Select value={formData.mode} onValueChange={(value) => handleChange('mode', value)}>
                <SelectTrigger id="mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rw">{t('Read / Write')}</SelectItem>
                  <SelectItem value="r">{t('Read only')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="lang">{t('Language')}</Label>
            <Select value={formData.lang} onValueChange={(value) => handleChange('lang', value)}>
              <SelectTrigger id="lang">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="fr">🇫🇷 French</SelectItem>
                <SelectItem value="en">🇬🇧 English</SelectItem>
                <SelectItem value="es">🇪🇸 Spanish</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{t('Service Desk *')}</Label>
            {canEditServiceDesks ? (
              <>
                <MultiSelect
                  options={serviceDeskOptions}
                  selected={selectedServiceDesks}
                  onChange={handleServiceDesksChange}
                  placeholder={t('Select the Service Desks…')}
                  allOption={{ value: '*', label: t('All Service Desks') }}
                />
                <p className="text-xs text-muted-foreground">
                  {t('Select one or more Service Desks, or "All" to grant access to all')}
                </p>
              </>
            ) : (
              <>
                <Input
                  id="serviceDeskId"
                  value={formData.serviceDeskId}
                  disabled
                  className="bg-gray-100"
                />
                <p className="text-xs text-muted-foreground">
                  {t('The user will be created with your Service Desk')}
                </p>
              </>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="sectors">{t('Sectors')}</Label>
            <Textarea
              id="sectors"
              value={formData.sectors}
              onChange={(e) => handleChange('sectors', e.target.value)}
              placeholder={t('Ex: Sector A, Sector B, Sector C')}
              rows={3}
            />
            <p className="text-xs text-muted-foreground">
              {t('Enter the sectors separated by commas')}
            </p>
          </div>

          {abilities.canGrantPermissions && (
            <div className="space-y-2">
              <Label>{t('Additional permissions')}</Label>
              {availablePermissionOptions.length > 0 ? (
                <MultiSelect
                  options={availablePermissionOptions}
                  selected={formData.permissions}
                  onChange={handlePermissionsChange}
                  placeholder={t('Select additional permissions…')}
                />
              ) : (
                <p className="text-sm text-muted-foreground italic">
                  {t('This role already has all available permissions.')}
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                {t('Default permissions for the role')} "{formData.role}" : {defaultPermissionsForRole.length > 0
                  ? defaultPermissionsForRole.join(', ')
                  : t('none')}
              </p>
            </div>
          )}

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}
          </div>

          <DialogFooter className="flex-shrink-0 pt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              {t('Cancel')}
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('Create the user')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
